import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  TrendingUp, Sparkles, Loader2, ExternalLink, Check, Building2, Disc, Play, Radio,
  Film, Youtube, Heart, ShoppingBag, SlidersHorizontal, Package, Video, GraduationCap, Badge,
  ArrowRight, Lock, DollarSign,
} from 'lucide-react';
import { incomeStreams, incomeUpdate, incomeAdvisor } from '../lib/api';
import { useApp } from '../context/AppContext';

const ICONS = {
  building: Building2, disc: Disc, play: Play, radio: Radio, film: Film, youtube: Youtube,
  heart: Heart, 'shopping-bag': ShoppingBag, sliders: SlidersHorizontal, package: Package,
  video: Video, graduation: GraduationCap, badge: Badge, sparkles: Sparkles,
};

const STATUS_COLORS = {
  new:        { bg: 'rgba(255,255,255,0.06)', fg: '#ffffffaa', border: 'rgba(255,255,255,0.15)', label: 'Not started' },
  discovered: { bg: 'rgba(167,139,250,0.14)', fg: '#a78bfa',   border: 'rgba(167,139,250,0.45)', label: 'Discovered' },
  applied:    { bg: 'rgba(255,207,82,0.14)',  fg: '#ffcf52',   border: 'rgba(255,207,82,0.45)',  label: 'Applied' },
  earning:    { bg: 'rgba(52,211,153,0.14)',  fg: '#34d399',   border: 'rgba(52,211,153,0.45)',  label: 'Earning' },
};

export default function Income() {
  const { user } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState('all');

  const load = async () => {
    setLoading(true);
    try { setData(await incomeStreams()); }
    catch { toast.error('Could not load income streams'); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return activeCat === 'all' ? data.streams : data.streams.filter(s => s.category === activeCat);
  }, [data, activeCat]);

  const stats = useMemo(() => {
    if (!data) return { earning: 0, applied: 0, discovered: 0 };
    return {
      earning:    data.streams.filter(s => s.status === 'earning').length,
      applied:    data.streams.filter(s => s.status === 'applied').length,
      discovered: data.streams.filter(s => s.status === 'discovered').length,
      total_lifetime: data.streams.reduce((sum, s) => sum + (s.lifetime_total_usd || 0), 0),
    };
  }, [data]);

  const updateStream = async (streamId, status) => {
    try {
      await incomeUpdate(streamId, { status });
      setData(d => ({ ...d, streams: d.streams.map(s => s.id === streamId ? { ...s, status } : s) }));
      toast.success(`Marked as ${status}`);
    } catch { toast.error('Could not update'); }
  };

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-20"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  if (!data) return null;

  return (
    <div data-testid="income-page" className="max-w-[1200px] mx-auto">
      <header className="mb-7">
        <div className="chip"><DollarSign className="w-3 h-3" /> Income Discovery</div>
        <h1 className="font-display text-[32px] sm:text-[40px] md:text-[52px] mt-3 leading-none">
          Every way an indie artist <span className="text-[#34d399]">actually</span> gets paid.
        </h1>
        <p className="text-white/65 text-[14px] md:text-[15px] mt-3 max-w-[680px] leading-relaxed">
          Most artists know about 3 of the 15+ income streams available to them. We list every one with eligibility, time-to-payout, and a one-click sign-up — then track your progress.
        </p>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-7">
        <StatCard icon={Check}  color="#34d399" label="Earning" value={stats.earning} />
        <StatCard icon={Sparkles} color="#ffcf52" label="Applied" value={stats.applied} />
        <StatCard icon={TrendingUp} color="#a78bfa" label="Discovered" value={stats.discovered} />
        <StatCard icon={DollarSign} color="#22d3ee" label="Lifetime tracked"
                  value={'$' + Number(stats.total_lifetime || 0).toLocaleString()} />
      </section>

      <AdvisorPanel user={user} streams={data.streams} />

      <div className="flex flex-wrap gap-1.5 mb-4 mt-7">
        <CatChip active={activeCat === 'all'} onClick={() => setActiveCat('all')}>All ({data.total})</CatChip>
        {data.categories.map(c => (
          <CatChip key={c.id} active={activeCat === c.id} onClick={() => setActiveCat(c.id)}>
            {c.label} ({data.streams.filter(s => s.category === c.id).length})
          </CatChip>
        ))}
      </div>

      <ul className="grid md:grid-cols-2 gap-3" data-testid="income-stream-list">
        {filtered.map(s => <StreamCard key={s.id} s={s} onMark={updateStream} />)}
      </ul>
    </div>
  );
}

function StatCard({ icon: Ic, color, label, value }) {
  return (
    <div className="card-shell p-4">
      <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: color + '22', color }}>
        <Ic className="w-4 h-4" />
      </div>
      <div className="mt-2 font-display text-[24px] leading-none">{value}</div>
      <div className="text-[11.5px] text-white/55 mt-1">{label}</div>
    </div>
  );
}

function CatChip({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`chip text-[12px] ${active ? 'active' : ''}`}>{children}</button>
  );
}

function StreamCard({ s, onMark }) {
  const Ic = ICONS[s.icon] || Sparkles;
  const sc = STATUS_COLORS[s.status] || STATUS_COLORS.new;
  return (
    <li className="card-shell p-4 flex flex-col gap-3" data-testid={`income-stream-${s.id}`}>
      <header className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: 'rgba(34,211,238,0.12)', color: '#22d3ee' }}>
          <Ic className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10.5px] uppercase tracking-wider font-bold text-white/55">{s.category}</span>
            <span className="chip text-[10px] capitalize">{s.difficulty}</span>
            <span className="chip text-[10px]" style={{ background: sc.bg, color: sc.fg, borderColor: sc.border }}>{sc.label}</span>
          </div>
          <h3 className="font-display text-[16px] mt-0.5 leading-tight">{s.name}</h3>
          <p className="text-[12.5px] text-white/65 mt-1 leading-relaxed">{s.short_desc}</p>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-2 text-[11.5px]">
        <Field label="Range" value={s.typical_range} />
        <Field label="Payout" value={s.time_to_payout} />
      </div>
      <p className="text-[11.5px] text-white/50 leading-relaxed"><span className="text-white/70">Eligibility:</span> {s.eligibility}</p>

      <div className="flex flex-wrap gap-2 items-center pt-1 border-t border-white/5">
        <a href={s.signup_url} target={s.signup_url.startsWith('/') ? '_self' : '_blank'} rel="noreferrer noopener"
           className="btn-primary text-[12px]" data-testid={`income-cta-${s.id}`}>
          <ExternalLink className="w-3.5 h-3.5" /> {s.cta}
        </a>
        {s.related_track_id && (
          <Link to="/reapercast" className="btn-ghost text-[12px]"><GraduationCap className="w-3.5 h-3.5" /> {s.related_track_title}</Link>
        )}
        <div className="ml-auto flex gap-1">
          {['discovered', 'applied', 'earning'].map(st => (
            <button key={st} onClick={() => onMark(s.id, st)}
                    className={`text-[11px] px-2 py-1 rounded-full border ${s.status === st ? '' : 'text-white/55 hover:text-white border-white/15 hover:border-white/30'}`}
                    style={s.status === st ? { background: STATUS_COLORS[st].bg, color: STATUS_COLORS[st].fg, borderColor: STATUS_COLORS[st].border } : undefined}
                    data-testid={`income-mark-${s.id}-${st}`}>
              {st === 'discovered' ? 'Saw it' : st === 'applied' ? 'Applied' : 'Earning'}
            </button>
          ))}
        </div>
      </div>
    </li>
  );
}

function Field({ label, value }) {
  return (
    <div className="bg-white/[0.025] rounded-lg px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-wider text-white/45">{label}</div>
      <div className="text-[12px] mt-0.5">{value}</div>
    </div>
  );
}

function AdvisorPanel({ user, streams }) {
  const tier = (user?.reapercast_tier || 'free').toLowerCase();
  const locked = !['starter', 'pro', 'label'].includes(tier);
  const [focus, setFocus] = useState('all');
  const [loading, setLoading] = useState(false);
  const [recs, setRecs] = useState(null);

  const ask = async () => {
    setLoading(true);
    try {
      const data = await incomeAdvisor({ focus, quick_wins: true });
      setRecs(data);
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || err?.response?.data?.detail || 'Advisor failed';
      toast.error(typeof msg === 'string' ? msg : 'Advisor failed');
    }
    setLoading(false);
  };

  if (locked) {
    return (
      <div className="card-shell p-5 mb-5 flex items-center gap-4 flex-wrap" style={{ borderColor: 'rgba(167,139,250,0.45)', background: 'linear-gradient(135deg, rgba(167,139,250,0.1), rgba(34,211,238,0.05))' }} data-testid="income-advisor-locked">
        <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: 'rgba(167,139,250,0.18)', color: '#a78bfa' }}>
          <Lock className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-[280px]">
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#a78bfa]">Pro feature</div>
          <h3 className="font-display text-[18px] mt-0.5">AI Income Advisor</h3>
          <p className="text-[12.5px] text-white/65 mt-1">Tell us your catalog + focus, get the top 3 income streams to chase first.</p>
        </div>
        <Link to="/memberships" className="btn-primary text-[12.5px]"><Sparkles className="w-3.5 h-3.5" /> Unlock from $9.99/mo</Link>
      </div>
    );
  }

  return (
    <div className="card-shell p-5 mb-5" data-testid="income-advisor">
      <div className="flex items-center gap-2 mb-1">
        <span className="w-9 h-9 rounded-xl grid place-items-center" style={{ background: 'rgba(34,211,238,0.18)', color: '#22d3ee' }}><Sparkles className="w-4 h-4" /></span>
        <div>
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#22d3ee]">AI Income Advisor</div>
          <h3 className="font-display text-[18px] leading-tight">What should I chase first?</h3>
        </div>
      </div>
      <p className="text-[12.5px] text-white/60 mt-1.5 leading-relaxed">Personalized to your catalog. Pick a focus — get 3 prioritized streams with first-step actions.</p>
      <div className="mt-3 flex flex-wrap gap-2 items-center">
        <select value={focus} onChange={e => setFocus(e.target.value)} className="bg-white/[0.05] border border-white/15 rounded-full px-3 py-1.5 text-[12.5px] focus:outline-none" data-testid="income-focus">
          <option value="all">All categories</option>
          <option value="royalties">Royalties</option>
          <option value="licensing">Licensing</option>
          <option value="direct">Direct-to-fan</option>
          <option value="services">Services</option>
        </select>
        <button onClick={ask} disabled={loading} className="btn-primary text-[12.5px]" data-testid="income-advisor-go">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {loading ? 'Thinking…' : 'Get my 3 priorities'}
        </button>
      </div>
      {recs?.recommendations?.length > 0 && (
        <div className="mt-4 grid gap-2.5" data-testid="income-recs">
          {recs.context && <div className="text-[11px] text-white/50">Based on: {recs.context}</div>}
          {recs.recommendations.map((r, i) => (
            <div key={r.stream_id} className="bg-white/[0.03] rounded-lg p-3 border border-white/8">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold" style={{ background: '#22d3ee22', color: '#22d3ee' }}>{i + 1}</span>
                <div className="font-semibold text-[14px]">{r.stream?.name || r.stream_id}</div>
                {r.estimated_first_payout_window && <span className="chip text-[10px]">First $: {r.estimated_first_payout_window}</span>}
              </div>
              <p className="text-[12.5px] text-white/70 mt-1.5 leading-relaxed">{r.why}</p>
              {r.first_step && <p className="text-[12px] text-white/55 mt-1.5 flex items-start gap-1.5"><ArrowRight className="w-3 h-3 mt-0.5 shrink-0 text-[#22d3ee]" /> {r.first_step}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
